import type { Node, XYPosition } from 'reactflow';

// The React Flow setNodes updater shape: takes the current nodes, returns the
// next ones. Named so the onFrame type below reads as "hand me setNodes".
type NodesUpdater = (nodes: Node[]) => Node[];

// Standard easeInOutCubic (see easings.net): the first half eases in (4t³), the
// second half mirrors it to ease out. Keeps the tidy-up motion from looking
// robotic (linear) without overshooting.
export function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

interface AnimateNodePositionsArgs {
  // Where each node starts and where it should end up, keyed by node id.
  from: Map<string, XYPosition>;
  to: Map<string, XYPosition>;
  durationMs: number;
  // Called each frame with a setNodes-style updater that maps the current nodes
  // to their interpolated positions. Pass React Flow's setNodes directly.
  onFrame: (update: NodesUpdater) => void;
  // Called once at the end so the caller can land exact positions and refit.
  onDone: () => void;
}

/**
 * Tween node positions from `from` to `to` over `durationMs`, driving React
 * Flow's setNodes each frame. React Flow v11 has no built-in position
 * transition, so we interpolate by hand. Returns a cancel function.
 *
 * Nodes missing from either map are left where they are, so a partial layout
 * (e.g. a selection) only moves the nodes it knows about.
 */
export function animateNodePositions({
  from,
  to,
  durationMs,
  onFrame,
  onDone,
}: AnimateNodePositionsArgs): () => void {
  let frame: number | null = null;
  let startTime: number | null = null;

  const step = (now: number) => {
    // Anchor the clock to the first frame's rAF timestamp so progress starts at
    // 0 (now is a rAF timestamp, not wall-clock, so we can't capture it upfront).
    if (startTime === null) {
      startTime = now;
    }
    const progress = Math.min((now - startTime) / durationMs, 1);
    const eased = easeInOutCubic(progress);

    onFrame((nodes) =>
      nodes.map((node) => {
        const start = from.get(node.id);
        const target = to.get(node.id);
        if (!start || !target) {
          return node;
        }
        return {
          ...node,
          position: {
            x: start.x + (target.x - start.x) * eased,
            y: start.y + (target.y - start.y) * eased,
          },
        };
      }),
    );

    if (progress < 1) {
      frame = requestAnimationFrame(step);
    } else {
      frame = null;
      onDone();
    }
  };

  frame = requestAnimationFrame(step);

  return () => {
    if (frame !== null) {
      cancelAnimationFrame(frame);
    }
  };
}
